import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Loader2,
  Building2,
  Mail,
  MapPin,
  Users,
  DollarSign,
  Cpu,
  Download,
  Target,
  TrendingUp,
  Clock,
  Zap,
  Leaf,
  Shield,
  Droplets,
  Recycle,
  FileText,
  AlertCircle,
  CheckCircle,
  Package,
  Truck,
  BarChart3,
  Globe,
  Phone,
  User,
  Award,
  Factory,
  CircleDot,
  GitBranch,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { SupplierSupplyChain } from "@/components/SupplyChainMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_HORIZONTAL =
  "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";

/* ─── helpers ─── */
const fmtCurrency = (v) => {
  if (v == null) return "N/A";
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Number(v).toLocaleString()}`;
};
const fmtNumber = (v) => (v != null ? Number(v).toLocaleString() : "N/A");
const fmtPct = (v) => (v != null ? `${v}%` : "N/A");

const tierColor = {
  "Tier 1": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Tier 2": "bg-amber-100 text-amber-800 border-amber-200",
  "Tier 3": "bg-red-100 text-red-800 border-red-200",
};
const maturityColor = {
  Leading: "bg-emerald-100 text-emerald-800",
  Mature: "bg-blue-100 text-blue-800",
  Developing: "bg-amber-100 text-amber-800",
  "High Attention Needed": "bg-orange-100 text-orange-800",
  "Critical Risk": "bg-red-100 text-red-800",
  "Not Assessed": "bg-gray-100 text-gray-800",
};
const priorityColor = {
  "P0 - Critical": "bg-red-100 text-red-800",
  "P1 - High": "bg-orange-100 text-orange-800",
  "P2 - Medium": "bg-amber-100 text-amber-800",
  "P3 - Low": "bg-blue-100 text-blue-800",
};
const severityStyle = {
  critical: { card: "border-l-red-500 bg-red-50/50", badge: "bg-red-100 text-red-800" },
  high: { card: "border-l-orange-500 bg-orange-50/50", badge: "bg-orange-100 text-orange-800" },
  medium: { card: "border-l-amber-500 bg-amber-50/50", badge: "bg-amber-100 text-amber-800" },
  low: { card: "border-l-blue-500 bg-blue-50/50", badge: "bg-blue-100 text-blue-800" },
};
const categoryIcon = {
  Governance: Shield, Climate: Leaf, Water: Droplets, Waste: Recycle,
  Circularity: Recycle, "Labor Standards": Users, "Health & Safety": Shield,
  "Diversity & Inclusion": Users, Ethics: FileText, Cybersecurity: Shield,
};

/* ─── Stat card ─── */
const StatCard = ({ icon: Icon, label, value, sub, color = "text-forest" }) => (
  <div className="p-4 bg-white rounded-xl border border-border/50 space-y-1" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

/* ─── KPI gauge mini ─── */
const KPICard = ({ label, value, unit, status }) => {
  const statusClr =
    status === "good" ? "text-emerald-600" : status === "warn" ? "text-amber-600" : "text-red-600";
  return (
    <div className="p-5 bg-white rounded-xl border border-border/50 text-center" data-testid={`kpi-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{label}</p>
      <p className={`text-3xl font-bold ${statusClr}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{unit}</p>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */
const AdminSupplierDetail = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [improvementData, setImprovementData] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [isExporting, setIsExporting] = useState(false);
  const [supplyChainData, setSupplyChainData] = useState(null);
  const [isLoadingSC, setIsLoadingSC] = useState(false);

  useEffect(() => { loadSupplierData(); }, [supplierId]);

  const loadSupplyChain = async () => {
    if (supplyChainData) return;
    setIsLoadingSC(true);
    try {
      const res = await axios.get(`${API}/admin/supply-chain/${supplierId}`);
      setSupplyChainData(res.data);
    } catch {
      toast.error("Failed to load supply chain data");
    } finally {
      setIsLoadingSC(false);
    }
  };

  const loadSupplierData = async () => {
    try {
      const [detailsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/admin/questionnaire/${supplierId}`),
        axios.get(`${API}/admin/improvement-actions/${supplierId}`),
      ]);
      setData(detailsRes.data);
      setImprovementData(actionsRes.data);
    } catch {
      toast.error("Failed to load supplier data");
      navigate("/admin");
    } finally {
      setIsLoading(false);
    }
  };

  const exportPDF = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export/pdf/${supplierId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `${data?.supplier?.company_name || "supplier"}_ESG_Scorecard.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PDF scorecard downloaded");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-forest animate-spin" />
      </div>
    );
  }

  const { supplier, questionnaire } = data || {};
  const profile = questionnaire?.supplier_profile || {};
  const erp = questionnaire?.erp_data || {};
  const scores = questionnaire?.esg_scores || {};
  const kpi = erp?.logistics_kpi || {};

  return (
    <div className="min-h-screen bg-cream">
      {/* ── Header ── */}
      <header className="glass-header sticky top-0 z-50" data-testid="detail-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="detail-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="detail-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
            </Link>
            <Button onClick={exportPDF} disabled={isExporting} className="btn-primary" data-testid="export-pdf-btn">
              {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF Scorecard
            </Button>
          </div>
        </div>
      </header>

      <main className="container-main py-8 space-y-6">
        {/* ── Page title row ── */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="supplier-header">
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-3xl font-bold text-foreground">
                {supplier?.company_name || "Supplier"}
              </h1>
              {profile.supplier_tier && (
                <Badge className={`text-sm px-3 py-0.5 border ${tierColor[profile.supplier_tier] || "bg-gray-100"}`} data-testid="tier-badge">
                  {profile.supplier_tier}
                </Badge>
              )}
              <Badge className={maturityColor[scores.maturity_tier] || maturityColor["Not Assessed"]} data-testid="maturity-badge">
                {scores.maturity_tier || "Not Assessed"}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              {profile.headquarters_city && (
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{profile.headquarters_city}, {profile.country}</span>
              )}
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{supplier?.email}</span>
              {profile.core_capability && (
                <span className="flex items-center gap-1"><Cpu className="w-3.5 h-3.5" />{profile.core_capability}</span>
              )}
            </div>
          </div>

          {/* ESG score summary pill */}
          <div className="flex items-center gap-6 bg-white rounded-xl px-6 py-4 border border-border/50 shadow-sm" data-testid="esg-summary-pill">
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Overall</p>
              <p className="text-3xl font-bold text-forest">{scores.overall_score?.toFixed(1) || "N/A"}</p>
            </div>
            <div className="h-10 w-px bg-border" />
            <div className="flex gap-4">
              {[
                { label: "E", val: scores.environmental_score, clr: "text-emerald-600" },
                { label: "S", val: scores.social_score, clr: "text-blue-600" },
                { label: "G", val: scores.governance_score, clr: "text-purple-600" },
              ].map((p) => (
                <div key={p.label} className="text-center">
                  <p className="text-xs text-muted-foreground">{p.label}</p>
                  <p className={`text-lg font-bold ${p.clr}`}>{p.val?.toFixed(0) || 0}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-border p-1 rounded-lg flex-wrap" data-testid="detail-tabs">
            <TabsTrigger value="profile" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />Profile
            </TabsTrigger>
            <TabsTrigger value="erp" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />ERP & Procurement
            </TabsTrigger>
            <TabsTrigger value="scorecard" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" />ESG Scorecard
            </TabsTrigger>
            <TabsTrigger value="improvement" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />Improvement Plan
            </TabsTrigger>
            <TabsTrigger value="flags" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <AlertCircle className="w-4 h-4 mr-2" />Risk Flags
            </TabsTrigger>
            <TabsTrigger value="supplychain" className="data-[state=active]:bg-forest data-[state=active]:text-white" onClick={loadSupplyChain}>
              <GitBranch className="w-4 h-4 mr-2" />Supply Chain
            </TabsTrigger>
          </TabsList>

          {/* ══════════ PROFILE TAB ══════════ */}
          <TabsContent value="profile" className="space-y-6" data-testid="tab-profile">
            {/* Supplier Overview grid */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-forest" /> Supplier Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={CircleDot} label="Supplier Tier" value={profile.supplier_tier || "N/A"} />
                  <StatCard icon={Globe} label="Country" value={profile.country || "N/A"} sub={profile.headquarters_city} />
                  <StatCard icon={Users} label="Employees" value={fmtNumber(profile.employees)} />
                  <StatCard icon={DollarSign} label="Revenue" value={fmtCurrency(profile.revenue_usd)} />
                  <StatCard icon={Factory} label="Core Capability" value={profile.core_capability || "N/A"} />
                  <StatCard icon={Award} label="Customer Category" value={profile.customer_category || "N/A"} />
                  <StatCard icon={BarChart3} label="Criticality" value={supplier?.criticality_level || "N/A"} />
                  <StatCard icon={DollarSign} label="Annual Spend" value={fmtCurrency(supplier?.annual_spend)} color="text-blue-700" />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-forest" /> Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Sustainability Lead</p>
                    <p className="font-medium">{profile.sustainability_lead || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Job Title</p>
                    <p className="font-medium">{profile.job_title || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                    <p className="font-medium flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{profile.email || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Phone</p>
                    <p className="font-medium flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{profile.country_code} {profile.phone_number || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Headquarters</p>
                    <p className="font-medium">{profile.headquarters_address || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ESG Category Scores summary */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-forest" /> ESG Summary Scores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-6">
                  {[
                    { label: "Environmental", score: scores.environmental_score, clr: "bg-emerald-500", icon: Leaf },
                    { label: "Social", score: scores.social_score, clr: "bg-blue-500", icon: Users },
                    { label: "Governance", score: scores.governance_score, clr: "bg-purple-500", icon: Shield },
                  ].map((p) => (
                    <div key={p.label} className="space-y-2" data-testid={`esg-summary-${p.label.toLowerCase()}`}>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <p.icon className="w-4 h-4" />{p.label}
                        </span>
                        <span className="text-lg font-bold">{p.score?.toFixed(1) || 0}</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${p.clr} rounded-full transition-all`} style={{ width: `${Math.min(p.score || 0, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Certifications */}
            {questionnaire?.governance_strategy?.certifications?.length > 0 && (
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-forest" /> Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2" data-testid="certifications-list">
                    {questionnaire.governance_strategy.certifications.map((cert, i) => (
                      <Badge key={i} variant="secondary" className="bg-forest/10 text-forest px-3 py-1 text-sm">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ══════════ ERP & PROCUREMENT TAB ══════════ */}
          <TabsContent value="erp" className="space-y-6" data-testid="tab-erp">
            {/* ERP Source banner */}
            {(erp.erp_source || erp.reporting_year) && (
              <div className="flex items-center gap-4 bg-white px-5 py-3 rounded-lg border border-border/50 text-sm" data-testid="erp-source-banner">
                <span className="text-muted-foreground">ERP Source:</span>
                <span className="font-medium">{erp.erp_source || "N/A"}</span>
                <span className="text-muted-foreground ml-auto">Reporting Year:</span>
                <span className="font-medium">{erp.reporting_year || "N/A"}</span>
              </div>
            )}

            {/* Logistics KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="logistics-kpis">
              <KPICard
                label="On-Time Delivery"
                value={fmtPct(kpi.on_time_delivery)}
                unit="target ≥ 95%"
                status={kpi.on_time_delivery >= 95 ? "good" : kpi.on_time_delivery >= 90 ? "warn" : "bad"}
              />
              <KPICard
                label="Defect Rate"
                value={kpi.defect_rate != null ? `${kpi.defect_rate}%` : "N/A"}
                unit="target < 1%"
                status={kpi.defect_rate < 1 ? "good" : kpi.defect_rate < 2 ? "warn" : "bad"}
              />
              <KPICard
                label="Avg Lead Time"
                value={kpi.average_lead_time != null ? `${kpi.average_lead_time}` : "N/A"}
                unit="days"
                status={kpi.average_lead_time <= 21 ? "good" : kpi.average_lead_time <= 30 ? "warn" : "bad"}
              />
              <KPICard
                label="Annual Procurement"
                value={fmtCurrency(kpi.annual_procurement_spend)}
                unit="spend"
                status="good"
              />
            </div>

            {/* Supplied Items Table */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-forest" /> Current Year Supplied Items & Services
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {erp.supplied_items?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="supplied-items-table">
                      <thead>
                        <tr className="border-b border-border bg-cream/50">
                          <th className="text-left px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Item Code</th>
                          <th className="text-left px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Description</th>
                          <th className="text-left px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Category</th>
                          <th className="text-right px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Annual Qty</th>
                          <th className="text-center px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Unit</th>
                          <th className="text-right px-5 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">Annual Spend</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {erp.supplied_items.map((item, i) => (
                          <tr key={i} className="hover:bg-cream/30 transition-colors" data-testid={`item-row-${i}`}>
                            <td className="px-5 py-3 font-mono text-xs">{item.item_code}</td>
                            <td className="px-5 py-3 font-medium">{item.item_description}</td>
                            <td className="px-5 py-3"><Badge variant="outline" className="text-xs">{item.category}</Badge></td>
                            <td className="px-5 py-3 text-right font-mono">{fmtNumber(Math.round(item.annual_quantity))}</td>
                            <td className="px-5 py-3 text-center text-muted-foreground">{item.unit}</td>
                            <td className="px-5 py-3 text-right font-mono font-medium">{fmtCurrency(item.annual_spend_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-cream/60 font-semibold border-t-2 border-border">
                          <td colSpan={5} className="px-5 py-3 text-right">Total Annual Spend</td>
                          <td className="px-5 py-3 text-right font-mono">
                            {fmtCurrency(erp.supplied_items.reduce((s, i) => s + (i.annual_spend_usd || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground p-6 text-center">No supplied items data available</p>
                )}
              </CardContent>
            </Card>

            {/* Sustainability Attributes */}
            {erp.sustainability_attributes?.length > 0 && (
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-forest" /> Sustainability Attributes of Supplied Products
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="sustainability-attributes">
                    {erp.sustainability_attributes.map((attr, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-cream/40 rounded-lg border border-border/30">
                        <div className="w-2 h-2 rounded-full bg-forest flex-shrink-0" />
                        <div>
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">{attr.product}</span>
                          <p className="font-medium text-sm">{attr.attribute}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ══════════ ESG SCORECARD TAB ══════════ */}
          <TabsContent value="scorecard" className="space-y-6" data-testid="tab-scorecard">
            {/* Pillar Scores */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Environmental", score: scores.environmental_score, icon: Leaf, gradient: "from-emerald-50 to-white", clr: "text-emerald-700" },
                { label: "Social", score: scores.social_score, icon: Users, gradient: "from-blue-50 to-white", clr: "text-blue-700" },
                { label: "Governance", score: scores.governance_score, icon: Shield, gradient: "from-purple-50 to-white", clr: "text-purple-700" },
                { label: "Resilience", score: scores.resilience_score, icon: Target, gradient: "from-amber-50 to-white", clr: "text-amber-700" },
              ].map((p) => (
                <Card key={p.label} className={`border-border/50 bg-gradient-to-br ${p.gradient}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p.icon className={`w-6 h-6 ${p.clr}`} />
                      <span className={`text-2xl font-bold ${p.clr}`}>{p.score?.toFixed(1) || 0}</span>
                    </div>
                    <p className="text-sm font-medium">{p.label}</p>
                    <Progress value={p.score || 0} className="h-2 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Sub-scores */}
            <Card className="border-border/50">
              <CardHeader><CardTitle className="font-heading text-lg">Detailed Sub-Scores</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { label: "Climate & Energy", score: scores.climate_energy_score, icon: Leaf },
                    { label: "Water Stewardship", score: scores.water_stewardship_score, icon: Droplets },
                    { label: "Waste & Circularity", score: scores.waste_circularity_score, icon: Recycle },
                    { label: "Labor & Human Rights", score: scores.labor_human_rights_score, icon: Users },
                    { label: "Health & Safety", score: scores.health_safety_score, icon: Shield },
                    { label: "Data Privacy & Cyber", score: scores.data_privacy_cyber_score, icon: Shield },
                    { label: "Ethics & Compliance", score: scores.ethics_compliance_score, icon: FileText },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-cream/50 rounded-lg" data-testid={`subscore-${i}`}>
                      <div className="text-forest"><item.icon className="w-4 h-4" /></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.label}</p>
                        <Progress value={item.score || 0} className="h-1.5 mt-1" />
                      </div>
                      <span className="font-bold">{item.score?.toFixed(1) || 0}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Key metrics */}
            <Card className="border-border/50">
              <CardHeader><CardTitle className="font-heading text-lg">Key Environmental & Social Metrics</CardTitle></CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <StatCard icon={Leaf} label="Scope 1+2 Emissions"
                    value={questionnaire?.climate_emissions?.scope1_2_emissions ? `${fmtNumber(questionnaire.climate_emissions.scope1_2_emissions)} tCO2e` : "N/A"} />
                  <StatCard icon={Target} label="SBTi Status" value={questionnaire?.climate_emissions?.sbti_status || "N/A"} />
                  <StatCard icon={Droplets} label="Water Stress" value={questionnaire?.water_management?.water_stress_level || "N/A"} />
                  <StatCard icon={Recycle} label="Waste Diversion"
                    value={questionnaire?.waste_circularity?.waste_diversion_rate != null ? fmtPct(questionnaire.waste_circularity.waste_diversion_rate) : "N/A"} />
                  <StatCard icon={Users} label="Total Employees" value={fmtNumber(questionnaire?.social_responsibility?.total_employees)} />
                  <StatCard icon={Users} label="Women in Mgmt"
                    value={questionnaire?.social_responsibility?.women_management_percent != null ? fmtPct(questionnaire.social_responsibility.women_management_percent) : "N/A"} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ══════════ IMPROVEMENT PLAN TAB ══════════ */}
          <TabsContent value="improvement" className="space-y-6" data-testid="tab-improvement">
            {improvementData && (
              <Card className="border-border/50 bg-gradient-to-r from-forest/5 to-sage/5">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Current Score</p>
                      <p className="text-3xl font-bold text-forest">{improvementData.current_score?.toFixed(1)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Potential Uplift</p>
                      <p className="text-3xl font-bold text-emerald-600">+{improvementData.potential_score_uplift?.toFixed(1)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Target Score</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {Math.min((improvementData.current_score || 0) + (improvementData.potential_score_uplift || 0), 100).toFixed(1)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Actions Required</p>
                      <p className="text-3xl font-bold text-foreground">{improvementData.total_actions}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <h3 className="font-heading text-xl font-semibold">AI-Recommended Improvement Actions</h3>

            {improvementData?.improvement_actions?.length > 0 ? (
              improvementData.improvement_actions.map((action, index) => {
                const Icon = categoryIcon[action.category] || Target;
                return (
                  <Card key={action.action_id} className="border-border/50" data-testid={`action-${index}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center text-forest flex-shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={priorityColor[action.priority] || "bg-gray-100"}>{action.priority}</Badge>
                            <Badge variant="outline">{action.category}</Badge>
                            <Badge className="bg-emerald-100 text-emerald-800">
                              <Zap className="w-3 h-3 mr-1" />+{action.expected_score_uplift} pts
                            </Badge>
                          </div>
                          <h4 className="font-heading text-lg font-semibold">{action.action_title}</h4>
                          <p className="text-sm text-muted-foreground">{action.description}</p>
                          <div className="bg-cream/50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-forest mb-1">Why it matters:</p>
                            <p className="text-sm text-muted-foreground">{action.why_it_matters}</p>
                          </div>
                          {action.recommended_steps?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Recommended Steps:</p>
                              <ul className="space-y-1">
                                {action.recommended_steps.map((step, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className="w-5 h-5 bg-sage/20 rounded-full flex items-center justify-center text-sage text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                                    {step}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="flex items-center gap-6 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Timeline: {action.target_timeline}</span>
                            <span className="flex items-center gap-1"><Target className="w-4 h-4" />Effort: {action.estimated_effort}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="border-border/50">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                  <p className="text-lg font-medium">No Improvement Actions Required</p>
                  <p className="text-muted-foreground">This supplier meets all ESG requirements.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ══════════ RISK FLAGS TAB ══════════ */}
          <TabsContent value="flags" className="space-y-6" data-testid="tab-flags">
            {questionnaire?.ai_flags?.length > 0 ? (
              questionnaire.ai_flags.map((flag, index) => {
                const s = severityStyle[flag.severity] || severityStyle.medium;
                return (
                  <Card key={flag.flag_id} className={`border-l-4 ${s.card}`} data-testid={`flag-${index}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={s.badge}>{flag.severity?.toUpperCase()}</Badge>
                          <Badge variant="outline">{flag.risk_type}</Badge>
                        </div>
                      </div>
                      <p className="text-foreground mb-3">{flag.explanation}</p>
                      <div className="bg-white/50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-forest mb-1">Recommendation:</p>
                        <p className="text-sm text-muted-foreground">{flag.recommendation}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="border-border/50">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                  <p className="text-lg font-medium">No Risk Flags Detected</p>
                  <p className="text-muted-foreground">This supplier has no significant ESG risk alerts.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ══════════ SUPPLY CHAIN TAB ══════════ */}
          <TabsContent value="supplychain" className="space-y-6" data-testid="tab-supplychain">
            <SupplierSupplyChain data={supplyChainData} isLoading={isLoadingSC} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminSupplierDetail;
